import { Popover } from '@material-ui/core';
import React, { useState } from 'react';

import { SearchableList, TextButton } from '@ds';
import { DSArrowDownIcon, DSPlusIcon } from '@ds-icons';
import {
  usePanelLibraryCreateTemplateMutation,
  usePanelLibraryRemoveTemplateMutation,
  usePanelLibraryUpdateTemplateMutation
} from '@mpm-druid/api';
import {
  LibraryPanelTemplate,
  UnsavedLibraryPanelTemplate
} from '@mpm-druid/types';
import { usePopoverState } from '@shared/hooks';
import { PythonTemplateCreateModal } from './PythonTemplateCreateModal';
import { PythonTemplateSelectOption } from './PythonTemplateSelectOption';

import styles from './PythonTemplateSelect.module.scss';

type PythonTemplateSelectProps = {
  code: string;
  templates: (UnsavedLibraryPanelTemplate | LibraryPanelTemplate)[];
  selectedRevisionId?: string;
  selectedTemplateId?: string;
  onSelectTemplate: (templateId: string | null, revisionId?: string) => void;
};

export function PythonTemplateSelect({
  code,
  selectedRevisionId,
  selectedTemplateId,
  onSelectTemplate,
  templates
}: PythonTemplateSelectProps) {
  const {
    mutateAsync: createTemplateAsync,
    isLoading: isCreatingTemplate
  } = usePanelLibraryCreateTemplateMutation();
  const {
    mutateAsync: updateTemplateAsync,
    isLoading: isUpdatingTemplate
  } = usePanelLibraryUpdateTemplateMutation();
  const { mutate: removeTemplate } = usePanelLibraryRemoveTemplateMutation();

  const {
    openPopover,
    onClosePopover,
    closePopover,
    isPopoverOpened,
    anchorElement
  } = usePopoverState();

  const [isCreateTemplateOpen, setIsCreateTemplateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<
    UnsavedLibraryPanelTemplate | LibraryPanelTemplate | null
  >(null);

  const selectedTemplate =
    templates.find(template => template.id === selectedTemplateId) ||
    templates[0];

  const handleSelect = (templateId: string | null, revisionId?: string) => {
    onSelectTemplate(templateId, revisionId);
    closePopover();
  };

  const handleSubmitUpsertTemplate = async (name: string) => {
    try {
      if (editingTemplate) {
        if (!editingTemplate.id) return;

        await updateTemplateAsync({
          name,
          templateId: editingTemplate.id
        });
      } else {
        const newTemplate = await createTemplateAsync({
          code,
          name
        });
        onSelectTemplate(newTemplate.templateId);
      }
    } finally {
      setIsCreateTemplateOpen(false);
      setEditingTemplate(null);
      closePopover();
    }
  };

  const handleDeleteTemplate = (templateId: string | null) => {
    if (!templateId) return;

    removeTemplate({ templateId });
  };

  return (
    <>
      <TextButton
        data-test="template-select-button"
        onClick={openPopover}
        active={isPopoverOpened}
        type="primary"
        size="medium"
        PostfixIcon={<DSArrowDownIcon />}
        className={styles.templateBtnContainer}
      >
        <span className={styles.templateBtnPrefix}>Template:</span>
        {selectedTemplate?.name}
      </TextButton>

      <Popover
        data-test="template-select-popover"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          className: styles.templateSelectorContainer
        }}
        elevation={0}
        open={isPopoverOpened}
        anchorEl={anchorElement}
        onClose={onClosePopover}
      >
        <SearchableList itemList={templates} searchField="name">
          <SearchableList.Result>
            {(template: UnsavedLibraryPanelTemplate | LibraryPanelTemplate) => (
              <PythonTemplateSelectOption
                data={template}
                selectedRevisionId={selectedRevisionId}
                selectedTemplateId={selectedTemplateId ?? null}
                onClick={revisionId => handleSelect(template.id, revisionId)}
                onEditTemplate={() => setEditingTemplate(template)}
                onDeleteTemplate={() => handleDeleteTemplate(template.id)}
              />
            )}
          </SearchableList.Result>
          <SearchableList.NotFound />
          <SearchableList.Footer>
            <SearchableList.FooterActionLink
              text="Create new template"
              to="#"
              onClick={() => setIsCreateTemplateOpen(true)}
              PrefixIcon={<DSPlusIcon />}
            />
          </SearchableList.Footer>
        </SearchableList>
      </Popover>

      {isCreateTemplateOpen || editingTemplate ? (
        <PythonTemplateCreateModal
          isSubmitting={isCreatingTemplate || isUpdatingTemplate}
          onClose={() => {
            setIsCreateTemplateOpen(false);
            setEditingTemplate(null);
          }}
          onSubmit={handleSubmitUpsertTemplate}
          template={editingTemplate}
        />
      ) : null}
    </>
  );
}
